import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { User } from "pwapi";

import { logout } from "shared/actions/authActions";
import { wipeCaseDetail } from "shared/actions/caseDetailActions";
import { wipeCases } from "shared/actions/casesActions";
import { wipeSlideDetail } from "shared/actions/slideDetailActions";
import Menu from "shared/components/Menu";
import {
  useCanSeeFrontEndAdmin,
  useCanSeeLIS,
} from "shared/hooks/usePermissions";

import ImpersonationLogOutButton from "components/ImpersonationLogOutButton";
import LinkButton from "components/LinkButton";
import ApplicationState from "store/ApplicationState";

import "./ProfileMenu.css";

function getInitials(first?: string, last?: string, username?: string) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  if (f || l) return (f[0] || "") + (l[0] || "");
  return (username || "?").slice(0, 2);
}

const ProfileMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );
  const hasImpersonator: boolean = useSelector(
    ({ auth }: ApplicationState) => !!auth.access?.impersonator_id
  );
  const canSeeAdmin = useCanSeeFrontEndAdmin();
  const canSeeLIS = useCanSeeLIS();

  const handleLogout = () => {
    dispatch(logout());
    dispatch(wipeCases());
    dispatch(wipeCaseDetail());
    dispatch(wipeSlideDetail());
  };

  const handleSettings = () => {
    navigate("/settings");
  };

  if (!user) return null;

  const first = user.first_name;
  const last = user.last_name;
  const username = user.username || "";
  const fullName = `${user.first_name}\u00A0${user.last_name}`;
  const initials = getInitials(first, last, username).toUpperCase();

  return (
    <Menu className="ProfileMenu">
      <Menu.Trigger>
        <LinkButton className="ProfileMenu__trigger">
          {user.first_name || user.last_name ? (
            <span>{fullName}</span>
          ) : (
            <span>{user.username}</span>
          )}
        </LinkButton>
      </Menu.Trigger>

      <Menu.Items className="ProfileMenu__menu">
        <header className="ProfileMenu__header" role="presentation">
          <span className="ProfileMenu__name">{fullName}</span>
          <span className="ProfileMenu__initials" aria-hidden="true">
            {initials}
          </span>
        </header>
        <hr className="ProfileMenu__rule" />
        <Menu.Item className="ProfileMenu__item" onSelect={handleSettings}>
          Settings
        </Menu.Item>
        {canSeeLIS && (
          <Menu.Item
            as={Link}
            to={`${process.env.REACT_APP_BACKEND_PROTOCOL}://${process.env.REACT_APP_LIS_DOMAIN}`}
            target="_blank"
            className="ProfileMenu__item"
            rel="noopener noreferrer"
            aria-label="LIS (opens in a new window)"
          >
            LIS
          </Menu.Item>
        )}
        {canSeeAdmin && (
          <Menu.Item
            as={Link}
            to="/admin"
            target="_blank"
            className="ProfileMenu__item"
            rel="noopener noreferrer"
            aria-label="Admin (opens in a new window)"
          >
            Admin
          </Menu.Item>
        )}
        {hasImpersonator ? (
          <ImpersonationLogOutButton
            className="ProfileMenu__item ProfileMenu__item--danger"
            type="link"
          />
        ) : (
          <Menu.Item
            className="ProfileMenu__item ProfileMenu__item--danger"
            onSelect={handleLogout}
          >
            Log Out
          </Menu.Item>
        )}
      </Menu.Items>
    </Menu>
  );
};

export default ProfileMenu;
